import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  name: "", description: "", category_id: "", brand_id: "",
  images: [""], videos: [], specifications: [],
  pricing: { hourly: null, daily: null, weekly: null, monthly: null, yearly: null, custom_per_day: null, recurring_enabled: false, recurring_interval: null },
  deposit: 0, late_fee_per_day: 0, damage_charges: 0, stock: 1, locations: [], delivery_available: true, is_active: true,
};

export default function TenantProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = () => {
    api.get("/tenant/products").then(({ data }) => setProducts(data));
    api.get("/tenant/categories").then(({ data }) => setCats(data));
    api.get("/tenant/brands").then(({ data }) => setBrands(data));
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...EMPTY, ...p }); setOpen(true); };

  const save = async () => {
    try {
      const body = { ...editing };
      if (editing.id) await api.put(`/tenant/products/${editing.id}`, body);
      else await api.post("/tenant/products", body);
      toast.success("Saved");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/tenant/products/${id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="tenant-products-title">Products</h1>
          <p className="text-slate-500 text-sm mt-1">{products.length} listed.</p>
        </div>
        <Button onClick={openNew} data-testid="tenant-create-product-btn"><Plus className="h-4 w-4 mr-2" /> Add Product</Button>
      </div>

      <Card className="p-0 overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Daily / Monthly</th>
              <th className="text-left px-4 py-3">Stock</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8">No products yet</td></tr>}
            {products.map((p) => {
              const cat = cats.find((c) => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`tenant-product-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 items-center">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 object-cover rounded" />}
                      <div>
                        <div className="font-medium">{p.name}</div>
                        {p.seller_id && <div className="text-[10px] text-amber-700 uppercase tracking-wider">Marketplace</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{cat?.name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{inr(p.pricing?.daily)} / {inr(p.pricing?.monthly)}</td>
                  <td className="px-4 py-3">{p.stock}</td>
                  <td className="px-4 py-3"><span className={`pill ${p.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{p.is_active ? "Active" : "Hidden"}</span></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" data-testid={`tenant-product-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`tenant-product-delete-${p.id}`} onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
          {editing && <ProductForm form={editing} setForm={setEditing} cats={cats} brands={brands} onSave={save} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductForm({ form, setForm, cats, brands, onSave }) {
  const setP = (k, v) => setForm({ ...form, pricing: { ...form.pricing, [k]: v } });
  return (
    <div className="space-y-4">
      <div><Label>Name</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div><Label>Description</Label><Textarea rows={3} data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label>
          <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v })}>
            <SelectTrigger data-testid="product-cat-select"><SelectValue placeholder="Pick" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Brand</Label>
          <Select value={form.brand_id || ""} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
            <SelectTrigger data-testid="product-brand-select"><SelectValue placeholder="Pick" /></SelectTrigger>
            <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Product image</Label><ImageUpload value={form.images?.[0] || ""} onChange={(v) => setForm({ ...form, images: [v] })} kind="product" testId="product-image" /></div>
      <Card className="p-4 bg-slate-50 border-slate-200">
        <Label className="block mb-2 font-display">Rental Pricing (₹) — fill any that apply</Label>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Hourly" type="number" value={form.pricing.hourly ?? ""} onChange={(e) => setP("hourly", e.target.value ? +e.target.value : null)} data-testid="product-pricing-hourly" />
          <Input placeholder="Daily" type="number" value={form.pricing.daily ?? ""} onChange={(e) => setP("daily", e.target.value ? +e.target.value : null)} data-testid="product-pricing-daily" />
          <Input placeholder="Weekly" type="number" value={form.pricing.weekly ?? ""} onChange={(e) => setP("weekly", e.target.value ? +e.target.value : null)} />
          <Input placeholder="Monthly" type="number" value={form.pricing.monthly ?? ""} onChange={(e) => setP("monthly", e.target.value ? +e.target.value : null)} data-testid="product-pricing-monthly" />
          <Input placeholder="Yearly" type="number" value={form.pricing.yearly ?? ""} onChange={(e) => setP("yearly", e.target.value ? +e.target.value : null)} />
          <Input placeholder="Custom/day" type="number" value={form.pricing.custom_per_day ?? ""} onChange={(e) => setP("custom_per_day", e.target.value ? +e.target.value : null)} />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Switch checked={form.pricing.recurring_enabled} onCheckedChange={(v) => setP("recurring_enabled", v)} data-testid="product-recurring-switch" />
          <Label className="cursor-pointer">Enable recurring rental</Label>
        </div>
      </Card>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Deposit</Label><Input type="number" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: +e.target.value })} /></div>
        <div><Label>Late fee/day</Label><Input type="number" value={form.late_fee_per_day} onChange={(e) => setForm({ ...form, late_fee_per_day: +e.target.value })} /></div>
        <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: +e.target.value })} /></div>
      </div>
      <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="product-active-switch" /><Label>Visible to customers</Label></div>
      <Button className="w-full" onClick={onSave} data-testid="product-save-btn">Save Product</Button>
    </div>
  );
}
